import { useNode } from "@craftjs/core";
import { PageHero } from "@/components/site/Primitives";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { HeroBlockSettings } from "@/components/craft/settings/HeroBlockSettings";

type HeroBlockProps = {
  eyebrow?: string;
  title: string;
  backgroundImage?: string;
  backgroundMediaId?: string | null;
  overlayOpacity?: number;
  className?: string;
  style?: ResponsiveStyleProps;
};

export function HeroBlock({
  eyebrow,
  title,
  backgroundImage,
  overlayOpacity = 0,
  className = "",
  style = defaultStyleProps(),
}: HeroBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);

  return (
    <div
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        "relative",
        `craft-node-${id}`,
        styleClass,
        className,
        selected && "outline outline-2 outline-dashed outline-primary/40",
      )}
      style={{
        ...inlineStyle,
        ...(backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}),
      }}
    >
      {backgroundImage && overlayOpacity > 0 && (
        <div
          className="pointer-events-none absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
          aria-hidden
        />
      )}
      <div className="relative">
        <PageHero eyebrow={eyebrow} title={title} />
      </div>
    </div>
  );
}

HeroBlock.craft = {
  displayName: "Hero",
  props: {
    eyebrow: "",
    title: "Page title",
    backgroundImage: "",
    backgroundMediaId: null,
    overlayOpacity: 0,
    className: "",
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: HeroBlockSettings,
  },
};
